import {
  getCourseDocumentRef,
  type CourseDocument,
} from '@cip/core/firebase/client';
import {useDocumentDataOnce} from 'react-firebase-hooks/firestore';
import type {CourseId} from 'types/common';
import {useEffect} from 'react';

/**
 * Reads the globally-readable `course/{courseId}` Firestore document once.
 *
 * The course document is readable without auth, so public marketing surfaces
 * can use this to source copy (start date) and gating (the application-open
 * flags) from live data rather than hardcoding either.
 *
 * Backed by `useDocumentDataOnce`, which reads the document once and owns the
 * subscription lifecycle — no manual effect, cleanup, or stale-write race.
 *
 * Each call issues its own read, so a component that needs several fields
 * should call this once and derive them all from the returned document rather
 * than composing several field-specific hooks.
 *
 * @param courseId - Id of the `course/{courseId}` document to read.
 * @return The course document, or undefined while it is loading, when it does
 *   not exist, or when the read fails. Callers must handle undefined as
 *   "unknown" and fall back to their safest rendering.
 * @throws Never — a read failure is logged and yields undefined.
 */
export const useCourseDocOnce = (
  courseId: CourseId,
): CourseDocument | undefined => {
  const [course, , error] = useDocumentDataOnce(
    getCourseDocumentRef({courseId}),
  );

  useEffect(() => {
    if (error) {
      console.debug('[useCourseDocOnce] Failed to load course document', error);
    }
  }, [error]);

  return course;
};
